import { postNoticeAPI } from '@/src/apis/rest/notice';
import Button from '@/src/components/atom/Button';
import Editor from '@/src/components/atom/Editor';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { PostNoticeRequestBody } from '@/src/apis/rest/notice.dtos';
import { NAV_CONST } from '@/src/constants/nav';
import { usePrompt } from '@/src/hooks/usePrompt';
import PhonePreview from '@/src/components/molecule/PhonePreview';
import dayjs from 'dayjs';
import { OPTIONS_CONST } from '@/src/constants/options';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { useRef } from 'react';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';

const BACK_PATH = PATH.NOTICE_LIST;

const formErrorMessages = {
  title: '제목을 입력해주세요.',
  contents: '내용을 입력해주세요.',
};

const noticeFormSchema = () => {
  return z.object({
    title: z.string().min(1, formErrorMessages.title),
    contents: z.string().min(1, formErrorMessages.contents),
    categoryType: z.string(),
    showedYn: z.boolean(),
    fixedTopYn: z.boolean(),
  });
};

const DEFAULT_VALUES: PostNoticeRequestBody = {
  title: '',
  contents: '',
  categoryType: 'common',
  showedYn: true,
  fixedTopYn: true,
};

export default function NoticeCreatePage() {
  // util
  const navigate = useNavigate();
  const { toast } = useToast();

  // form
  const form = useForm<PostNoticeRequestBody>({
    resolver: zodResolver(noticeFormSchema()),
    defaultValues: DEFAULT_VALUES,
    reValidateMode: 'onSubmit',
  });

  // state
  const {
    Dialog: SaveDialog,
    isOpen: isSaveOpen,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  // mutation
  const postNoticeMutation = useMutation({ mutationFn: postNoticeAPI });

  // event
  const handleGoBack = () => {
    navigate(-1);
  };
  const handleSubmit = (values: PostNoticeRequestBody) => {
    postNoticeMutation.mutate(values, {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: TOAST_CONST.saveSuccess.title,
          description: TOAST_CONST.saveSuccess.description,
        });

        navigate(BACK_PATH);
      },
      onError: e => {
        toast({
          variant: 'error',
          title: TOAST_CONST.error.title,
          description: e.message,
        });
      },
    });
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Button
            type="button"
            onClick={openSaveDialog}
            className="w-40"
            buttonSize="medium"
            disabled={!form.formState.isValid}
            isLoading={postNoticeMutation.isPending}
          >
            <div className="flex items-center gap-x-2">{BUTTON_CONST.save}</div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  usePrompt(form.formState.isDirty && !form.formState.isSubmitSuccessful);

  return (
    <>
      <MainContentTitle
        breadcrumbList={[NAV_CONST.notice, NAV_CONST.noticeAdd]}
        pageTitle={NAV_CONST.noticeAdd}
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative mb-[100px]"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <SectionCard cardTitle="공지사항 내용">
            <div className="flex flex-col gap-y-4">
              <FormField
                control={form.control}
                name="categoryType"
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent label="유형" error={error}>
                      <RadioGroup
                        optionList={[
                          {
                            label: OPTIONS_CONST.commonCategory,
                            value: 'common',
                          },
                          {
                            label: OPTIONS_CONST.communityCategory,
                            value: 'community',
                          },
                        ]}
                        rootProps={{
                          ...field,
                          onValueChange: field.onChange,
                        }}
                      />
                    </FormContent>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="showedYn"
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent label="노출 여부" error={error}>
                      <RadioGroup
                        optionList={[
                          { label: OPTIONS_CONST.showed, value: 'true' },
                          { label: OPTIONS_CONST.notShowed, value: 'false' },
                        ]}
                        rootProps={{
                          value: field.value ? 'true' : 'false',
                          onValueChange: e => field.onChange(e === 'true'),
                        }}
                      />
                    </FormContent>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="fixedTopYn"
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent label="우선 노출 여부" error={error}>
                      <RadioGroup
                        optionList={[
                          { label: OPTIONS_CONST.applied, value: 'true' },
                          { label: OPTIONS_CONST.notApplied, value: 'false' },
                        ]}
                        rootProps={{
                          value: field.value ? 'true' : 'false',
                          onValueChange: e => field.onChange(e === 'true'),
                        }}
                      />
                    </FormContent>
                  );
                }}
              />
              <div className="flex gap-6">
                <div className="flex flex-1 flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          className="col-span-3"
                          label="제목"
                          isRequired
                          error={error}
                        >
                          <Input placeholder="제목을 입력해주세요" {...field} />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="contents"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          className="col-span-3"
                          label="내용"
                          isRequired
                          error={error}
                        >
                          <div className="flex gap-3">
                            <div className="flex-1">
                              <Editor
                                height={770}
                                placeholder="내용을 입력해주세요"
                                {...field}
                              />
                            </div>
                          </div>
                        </FormContent>
                      );
                    }}
                  />
                </div>
                <div className="w-[400px] flex-shrink-0">
                  <PhonePreview>
                    <div className="sticky top-0">
                      <img
                        src="/assets/images/img-notice-header-preview.png"
                        alt="img-notice-header-preview"
                        width="100%"
                        height="auto"
                      />
                      <PhonePreview.Header className="flex flex-col gap-1 bg-white">
                        <h2 className="break-keep text-h20sb">
                          {form.watch().title || '제목을 입력해주세요'}
                        </h2>
                        <p className="text-b14r text-gray400">
                          {dayjs().format('YYYY.MM.DD HH:mm')}
                        </p>
                      </PhonePreview.Header>
                    </div>
                    <PhonePreview.Content>
                      <div
                        className="ck-content"
                        dangerouslySetInnerHTML={{
                          __html:
                            form.watch().contents || '내용을 입력해주세요',
                        }}
                      />
                    </PhonePreview.Content>
                  </PhonePreview>
                </div>
              </div>
            </div>
          </SectionCard>
          {renderBottomFixedContent()}
        </form>
      </Form>

      {isSaveOpen && (
        <SaveDialog
          open={isSaveOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openSaveDialog();
            } else {
              closeSaveDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.save}
          isLoading={postNoticeMutation.isPending}
        />
      )}
    </>
  );
}
